import { ChangeDetectionStrategy, Component, OnInit, computed, inject, output } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { AuthStore } from '@app/core/auth/auth.store';
import { CurrentCompanyService, Permission, type PermissionValue } from '@app/core/services';
import { TranslatePipe } from '@ngx-translate/core';

interface SettingsNavItem {
  readonly labelKey: string;
  readonly route: string;
  readonly iconClass?: string;
  readonly permissions: readonly PermissionValue[];
}

@Component({
  selector: 'app-nav-settings',
  imports: [RouterLink, RouterLinkActive, TranslatePipe],
  templateUrl: './nav-settings.html',
  styleUrl: './nav-settings.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavSettings implements OnInit {
  private readonly authStore = inject(AuthStore);
  private readonly currentCompanyService = inject(CurrentCompanyService);
  private readonly router = inject(Router);

  readonly adminRequested = output<void>();

  protected readonly isExternalCompany = this.currentCompanyService.isExternalCompany;
  protected readonly currentCompany = this.currentCompanyService.currentCompany;
  protected readonly activeCompanyId = computed(() => this.currentCompany()?.companyId ?? null);
  protected readonly canAccessAdminNavigation = computed(() => this.authStore.hasAnyPermission([
    Permission.PERM_PLATFORM_TENANT_GET,
    Permission.PERM_PLATFORM_CONTRACT_GET,
    Permission.PERM_PLATFORM_SCHEDULED_TASK_GET,
    Permission.PERM_PLATFORM_INTEGRATION_GET,
    Permission.PERM_PLATFORM_CONFIGURATION_GET,
    Permission.PERM_PLATFORM_LICENSE_GET,
  ]));

  protected readonly moduleItems = computed<readonly SettingsNavItem[]>(() => this.filterItems([
    {
      labelKey: 'management.navigation.modules.chatbots',
      route: this.createCompanyRoute('/settings', 'chatbots'),
      iconClass: 'fa-regular fa-comment-dots',
      permissions: [Permission.PERM_MODULE_CHATBOT_GET],
    },
    {
      labelKey: 'management.navigation.modules.liveChat',
      route: this.createCompanyRoute('/settings', 'live-chat'),
      iconClass: 'fa-regular fa-comments',
      permissions: [Permission.PERM_MODULE_LIVE_CHAT_SETTINGS_GET],
    },
    {
      labelKey: 'management.navigation.modules.subjects',
      route: this.createCompanyRoute('/settings', 'subjects'),
      iconClass: 'fa-regular fa-bookmark',
      permissions: [Permission.PERM_MODULE_LIVE_CHAT_SETTINGS_GET],
    },
    {
      labelKey: 'management.navigation.modules.reports',
      route: this.createCompanyRoute('/settings', 'reports'),
      iconClass: 'fa-regular fa-chart-bar',
      permissions: [Permission.PERM_MODULE_REPORTING_GET],
    },
  ]));

  protected readonly settingsItems = computed<readonly SettingsNavItem[]>(() => this.filterItems([
    { labelKey: 'management.navigation.company.general', route: this.createCompanyRoute('/settings', 'general'), iconClass: 'fa-regular fa-pen-to-square', permissions: [Permission.PERM_TENANT_COMPANY_GET] },
    { labelKey: 'management.navigation.company.users', route: this.createCompanyRoute('/settings', 'usermanagement'), iconClass: 'fa-regular fa-address-card', permissions: [Permission.PERM_TENANT_USER_GET, Permission.PERM_TENANT_ROLE_GET, Permission.PERM_TENANT_TEAM_GET] },
    { labelKey: 'management.navigation.company.subscription', route: this.createCompanyRoute('/settings', 'subscription'), iconClass: 'fa-regular fa-credit-card', permissions: [Permission.PERM_TENANT_LICENSE_GET, Permission.PERM_TENANT_BILLING_GET] },
    { labelKey: 'management.navigation.company.integrations', route: this.createCompanyRoute('/settings', 'integrations'), iconClass: 'fa-regular fa-handshake', permissions: [Permission.PERM_TENANT_INTEGRATION_GET] },
  ]));

  ngOnInit(): void {
    void this.currentCompanyService.getCurrentCompany();
  }

  protected async resetCompanyContext(): Promise<void> {
    if (!this.isExternalCompany()) return;
    await this.currentCompanyService.resetToCurrentUser();
    void this.router.navigate(['/settings', 'general']);
  }

  protected onAdminRequested(): void {
    if (!this.canAccessAdminNavigation()) return;
    this.adminRequested.emit();
  }

  private filterItems(items: readonly SettingsNavItem[]): readonly SettingsNavItem[] {
    return items.filter((item) => this.authStore.hasAnyPermission(item.permissions));
  }

  private createCompanyRoute(prefix: string, leaf: string): string {
    const companyId = this.activeCompanyId();
    return companyId ? `${prefix}/${companyId}/${leaf}` : `${prefix}/${leaf}`;
  }
}
